import type { Plan } from './plans';

/**
 * The lines that a new plan starts with. The backend command
 * backfillCardPoints gives these lines to each plan with no stored lines,
 * and a third line, "Equal up and download speeds", to a DGtek plan (an id
 * that starts with "PA-" or "pa-").
 */
export const DEFAULT_CARD_POINTS: ReadonlyArray<string> = ['No lock in contract', 'Unlimited data'];

interface Speeds {
  download: number;
  upload: number;
}

/** The advertised speeds of the plan, as the card title shows them. */
export const planSpeeds = (plan: Plan): Speeds => ({
  download: plan.speed,
  upload: plan.upload > 0 ? plan.upload : plan.speed,
});

/**
 * The evening speeds that the preview shows. The signup site shows the pair
 * for the access technology of the building. The preview cannot know the
 * building, thus it shows the passive (GPON) pair. If a value is not set,
 * the plan speed is used for it, as on the signup site.
 */
export const previewEveningSpeeds = (plan: Plan): Speeds => {
  const advertised = planSpeeds(plan);
  return {
    download: plan.evening_download_passive || advertised.download,
    upload: plan.evening_upload_passive || advertised.upload,
  };
};

/**
 * The last line on each plan card. The operator cannot change or remove it.
 * The card shows the mark in superscript when "superscript" is true.
 */
export interface SpeedPoint extends Speeds {
  label: string;
  mark: string;
  superscript: boolean;
}

/**
 * The last line in the preview: the typical evening speed, or the maximum
 * theoretical speed when the evening speeds are not set or are the same as
 * the plan speeds. The signup site uses the same rule.
 */
export const previewSpeedPoint = (plan: Plan): SpeedPoint => {
  const advertised = planSpeeds(plan);
  const evening = previewEveningSpeeds(plan);
  if (evening.download === advertised.download && evening.upload === advertised.upload) {
    return {
      label: 'Max Theoretical speed', mark: '#', superscript: true, ...advertised,
    };
  }
  return {
    label: 'Typical evening speed', mark: '*', superscript: false, ...evening,
  };
};

/** The speed line as plain text, where the mark cannot be in superscript. */
export const speedPointText = (point: SpeedPoint): string => (
  `${point.label}${point.mark} ${point.download}/${point.upload}Mbps`
);

export const cardPointErrors = (plan: Plan): string[] => (plan.card_points ?? [])
  .map((point, i) => (point.trim() === '' ? `Plan card point ${i + 1} is empty` : ''))
  .filter(Boolean);

export const sameCardPoints = (a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean => (
  a.length === b.length && a.every((point, i) => point === b[i])
);
